import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import {
  FaChartBar,
  FaFileAlt,
  FaChartLine,
  FaCheckCircle,
  FaComments,
  FaStopwatch,
  FaSmile
} from 'react-icons/fa';
import { getCallDetails, genaiQuery, genairefresh, override, markAssessed, unmarkAssessed } from '../../api/api';
import { Formatter } from '../../format';
import { SentimentIcon, SentimentBar } from '../../components/SentimentIcon';
import { TrendIcon } from '../../components/TrendIcon';
import ChatInput from '../../components/ChatInput';
import CallDetailsPanel from '../../components/CallDetailsPanel';
import { SentimentChart } from '../../components/SentimentChart';
import { SpeakerTimeChart } from '../../components/SpeakerTimeChart';
import { TranscriptSegment } from '../../components/TranscriptSegment';
import { TranscriptOverlay } from '../../components/TranscriptOverlay';
import { getEntityColor } from '../../components/colours';
import './dashboard.css';
import RefreshButton from '../../components/RefreshButton';
import { Container, Header } from '@cloudscape-design/components';
import { LoudnessChart } from "./LoudnessChart";
import { ComprehendSentimentChart } from "./ComprehendSentimentChart";
import useIsSuperAdmin from '../../hooks/useIsSuperAdmin';

const Dashboard = ({ setAlert, userEmail }) => {
  const { key } = useParams();
  const history = useHistory();
  const { data: callData, error } = useSWR(
    key ? `/get/${key}` : null,
    () => getCallDetails(key)
  );
  const loading = !callData && !error;
  const isTranscribeCallAnalyticsMode =
    callData?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
      ?.TranscribeApiType === "analytics";
  const [loudnessData, setLoudnessData] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [genAiHistory, setGenAiHistory] = useState([]);
  const audioRef = useRef(null);
  const transcriptRef = useRef(null);
  const [speakerLabels, setSpeakerLabels] = useState({});
  const { mutate } = useSWRConfig();
  console.log("User email : ", userEmail)

  const range = (start, end) => {
    return [...Array(end + 1 - start).keys()].map((x) => x + start);
  };

  const toFiniteNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const sentimentLabelToScore = (label) => {
    if (!label || typeof label !== 'string') return null;
    const normalized = label.trim().toUpperCase();
    if (normalized === 'POSITIVE') return 1;
    if (normalized === 'NEGATIVE') return -1;
    if (normalized === 'NEUTRAL') return 0;
    if (normalized === 'MIXED') return 0;
    return null;
  };

  const extractScoreFromObject = (score) => {
    if (!score || typeof score !== 'object') return null;

    const positive = toFiniteNumber(score.Positive ?? score.positive);
    const negative = toFiniteNumber(score.Negative ?? score.negative);
    if (positive !== null || negative !== null) {
      return (positive ?? 0) - (negative ?? 0);
    }

    const overall =
      toFiniteNumber(score.Score ?? score.score ?? score.Overall ?? score.overall ?? score.Value ?? score.value);
    if (overall !== null) {
      return overall;
    }

    return null;
  };

  const normalizeFlag = (value) => {
    if (value === true || value === 'true') return 1;
    if (value === false || value === 'false' || value === null || value === undefined) return 0;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? 1 : 0;
  };

  const deriveSegmentSentimentScore = (segment) => {
    if (!segment) return 0;

    const scoreObjectValue = extractScoreFromObject(segment.SentimentScore);
    if (scoreObjectValue !== null) {
      const labelScore = sentimentLabelToScore(segment.Sentiment);
      if (labelScore !== null) {
        if (scoreObjectValue === 0) {
          return labelScore;
        }
        const haveSameDirection = Math.sign(scoreObjectValue) === Math.sign(labelScore);
        return haveSameDirection || Math.sign(labelScore) === 0
          ? scoreObjectValue
          : Math.abs(scoreObjectValue) * Math.sign(labelScore);
      }
      return scoreObjectValue;
    }

    const numericScore = toFiniteNumber(segment.SentimentScore);
    if (numericScore !== null) {
      const positiveFlag = normalizeFlag(segment.SentimentIsPositive);
      const negativeFlag = normalizeFlag(segment.SentimentIsNegative);
      const flagDelta = positiveFlag - negativeFlag;
      if (flagDelta !== 0) {
        return numericScore * Math.sign(flagDelta);
      }
      const labelScore = sentimentLabelToScore(segment.Sentiment);
      if (labelScore !== null) {
        return numericScore * (labelScore === 0 ? 0 : Math.sign(labelScore));
      }
      return numericScore;
    }

    const averageScore = toFiniteNumber(segment.AverageSentimentScore);
    if (averageScore !== null) {
      const labelScore = sentimentLabelToScore(segment.Sentiment);
      if (labelScore !== null && labelScore !== 0) {
        return averageScore * Math.sign(labelScore);
      }
      return averageScore;
    }

    const labelScore = sentimentLabelToScore(segment.Sentiment);
    if (labelScore !== null) {
      return labelScore;
    }

    const fallbackDelta =
      normalizeFlag(segment.SentimentIsPositive) - normalizeFlag(segment.SentimentIsNegative);
    if (fallbackDelta !== 0) {
      return Math.sign(fallbackDelta);
    }

    return 0;
  };

  const createLoudnessData = (segment) => {
    const derivedScore = deriveSegmentSentimentScore(segment);
    const sentimentBucket = derivedScore > 0 ? 5 : derivedScore < 0 ? -5 : 0;
    const start = Math.floor(segment.SegmentStartTime);
    const end = Math.floor(segment.SegmentEndTime);
    const r = range(start, end);
    return r.map((item, i) => ({
      x: item,
      y: segment.LoudnessScores[i],
      interruption: segment.SegmentInterruption && item === start ? 100 : null,
      sentiment: sentimentBucket,
      sentimentScore: derivedScore,
      silence: (segment.LoudnessScores[i] === 0 ? true : false)
    }));
  };

  const createSentimentData = (segment) => {
    const derivedScore = deriveSegmentSentimentScore(segment);
    const start = Math.floor(segment.SegmentStartTime);
    const end = Math.floor(segment.SegmentEndTime);
    const r = range(start, end);
    return r.map((item) => ({
      x: item,
      y: derivedScore
    }));
  }

  useEffect(() => {
    if (error) {
      console.error('Error fetching call details:', error);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to load call details. Please try again.'
      });
    }
  }, [error, setAlert]);

  useEffect(() => {
    const labels = callData?.ConversationAnalytics?.SpeakerLabels || [];
    const newSpeakerLabels = {
      NonTalkTime: "Silence",
      Interruptions: "Interruptions",
      Positive: "Positive",
      Negative: "Negative",
      Neutral: "Neutral"
    };
    labels.map(({ Speaker, DisplayText }) => {
      newSpeakerLabels[Speaker] = DisplayText;
    });
    setSpeakerLabels(newSpeakerLabels);

    /*setGenAiQueries(
      (data?.ConversationAnalytics?.GenAiQuery ?
        Object.entries(data?.ConversationAnalytics?.GenAiQuery).map(([key, value]) => {
          return {
            label: key,
            value: (value instanceof Array ? value.join(', ') : value)
          }
        }) : [])
    );*/
  }, [callData]);

  useEffect(() => {
    const loudness = {};

    if (isTranscribeCallAnalyticsMode) {
      // TCA mode
      let interruptions = [];
      let silence = [];
      let positive = [];
      let negative = [];
      let neutral = [];
      let nonSilence = [];

      Object.keys(speakerLabels).forEach(key => {
        let keyLoudness = (callData?.SpeechSegments || [])
        .filter((segment) => segment.SegmentSpeaker === key)
        .map(createLoudnessData)
        .flat();
        
        loudness[key] = keyLoudness;
        let newInterruptions = keyLoudness.filter((d) => d.interruption)
          .map((d) => ({ y: d.interruption, x: d.x }))
        interruptions = interruptions.concat(newInterruptions)

        let newSilence = keyLoudness.filter((d) => d.silence)
          .map((d) => ({ x: d.x, y: 100 }))
        silence = silence.concat(newSilence);

        keyLoudness.forEach((item) => {
          let sentimentItem = {
            x: item.x,
            y: 10,
            sentiment: item.sentiment
          };
          if (item.sentiment > 0) positive.push(sentimentItem)
          else if (item.sentiment < 0) negative.push(sentimentItem)
          else neutral.push(sentimentItem);
          nonSilence[item.x.toString()] = sentimentItem;
        });

      });
      
      // generate the rest of the silence
      if (callData) {
        const r = range(0, parseInt(callData?.ConversationAnalytics.Duration));
        r.map((item, i) => {
          if (!(i in nonSilence)) {
            silence = silence.concat({ x: i, y: 100 });
          }
        });
      }

      loudness['Interruptions'] = interruptions;
      loudness['NonTalkTime'] = silence;
      loudness['Positive'] = positive;
      loudness['Neutral'] = neutral;
      loudness['Negative'] = negative;
    } else {
      // this is transcribe standard
      Object.keys(speakerLabels).forEach(key => {
        if (key.indexOf('spk_') >= 0) {
          let keyLoudness = (callData?.SpeechSegments || [])
          .filter((segment) => segment.SegmentSpeaker === key)
          .map(createSentimentData)
          .flat();
          console.log('keyloudness', keyLoudness);
          loudness[key] = keyLoudness;
        }
      });
    }
    console.log('Loudness', loudness);
    setLoudnessData(loudness);
  }, [speakerLabels])

  // useEffect(() => {
  //   const labels = callData?.ConversationAnalytics?.SpeakerLabels || [];
  //   const mapping = {};
  //   labels.forEach(({ Speaker, DisplayText }) => {
  //     mapping[Speaker] = DisplayText;
  //   });
  //   setSpeakerLabels(mapping);
  // }, [callData]);

  const handleGenAiSubmit = async (text) => {
    if (!callData) return;
    const entry = { question: text, answer: '...' };
    setGenAiHistory((h) => [...h, entry]);
    try {
      const result = await genaiQuery(key || callData.key, text);
      setGenAiHistory((h) =>
        h.map((e) => (e === entry ? { ...e, answer: result.response } : e))
      );
    } catch (error) {
      setGenAiHistory((h) =>
        h.map((e) => (e === entry ? { ...e, answer: 'Error fetching answer' } : e))
      );
    }
  };

  const audioEndTimestamps = (callData?.SpeechSegments || [])
    .map(({ WordConfidence }) => WordConfidence)
    .flat()
    .reduce((acc, item) => [...acc, item.EndTime], []);

  const onAudioPlayTimeUpdate = () => {
    let elementEndTime;
    for (let i = 0; i < audioEndTimestamps.length; i++) {
      if (audioRef.current.currentTime < audioEndTimestamps[i]) {
        elementEndTime = audioEndTimestamps[i];
        break;
      }
    }

    [...transcriptRef.current.getElementsByClassName('playing')].forEach(elem =>
      elem.classList.remove('playing')
    );
    const el = transcriptRef.current.querySelector(
      `span[data-end="${elementEndTime}"]`
    );
    el && el.classList.add('playing');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rewardsco-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading call details...</p>
        </div>
      </div>
    );
  }

  if (!callData) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Call not found</h3>
        <p className="mt-1 text-sm text-gray-500">
          The requested call could not be found.
        </p>
        <div className="mt-6">
          <button
            onClick={() => history.push('/')}
            className="btn-primary"
          >
            Back to Call List
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FaChartBar /> },
    { id: 'transcript', label: 'Transcript', icon: <FaFileAlt /> },
    { id: 'analytics', label: 'Analytics', icon: <FaChartLine /> },
    { id: 'qa', label: 'QA Assessment', icon: <FaCheckCircle /> },
    // { id: 'genai', label: 'GenAI', icon: <FaComments /> }
  ];

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const genAiSummary = (() => {
    let summary = callData?.ConversationAnalytics?.Summary
      ? Object.entries(callData.ConversationAnalytics.Summary)
          .filter(([key]) => key !== 'Validations' && key !== 'Scores')  // Exclude validation data and scores - they're displayed in separate sections
          .map(([key, value]) => ({
            label: key,
            value: Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : value,
          }))
      : [];

    const contactSummary =
      callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
    if (contactSummary) {
      summary = [{ label: 'Contact Summary', value: contactSummary }, ...summary];
    }
    return summary;
  })();

  const refreshSummary = async (template) => {
    if (!callData) return;
    
    // Extract templateName from the template object, or use template directly if it's a string
    const templateName = template?.templateName || template || '';
    
    console.log("calldata key:", key);
    console.log("template received:", template);
    console.log("templateName to send:", templateName);
    
    await genairefresh(key || callData.filename, templateName);
    await mutate(`/get/${callData.key || callData.filename}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {callData.jobName ||
              callData?.ConversationAnalytics?.SourceInformation?.[0]?.TranscribeJobInfo?.TranscriptionJobName}
          </h1>
          <p className="text-gray-600 mt-1">
            Call Details • {Formatter.Timestamp(
              callData.timestamp || callData?.ConversationAnalytics?.ConversationTime
            )}
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => history.goBack()}
            className="btn-outline"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          
          <RefreshButton
            onRefresh={(template) => refreshSummary(template)}
            setAlert={setAlert}
            showTemplateDropdown={true}
            campaign={callData?.ConversationAnalytics?.Cust}
            currentTemplate={callData?.ConversationAnalytics?.Template}
            label="Refresh Call"
          />
        </div>
      </div>

      <CallDetailsPanel callData={callData} />

      {isTranscribeCallAnalyticsMode && (
        <div
        >            <h2 className="text-lg font-semibold mb-4">
              Loudness/Sentiment
            </h2>
          {!loudnessData && !error ? (
            <div key='noSpeakers'>No Speakers</div>
          ) : (
            <LoudnessChart loudnessData={loudnessData} speakerLabels={speakerLabels} />
          )}
        </div>
      )}
      {!isTranscribeCallAnalyticsMode && (
        <div
        >  
          <h2 className="text-lg font-semibold mb-4">
              Comprehend Sentiment
          </h2>
          {!loudnessData && !error ? (
            <div key='noSpeakers'>No Speakers</div>
          ) : (
            <ComprehendSentimentChart comprehendSentimentData={loudnessData} speakerLabels={speakerLabels} />
          )}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Quality Score (v2.0) or QA Score (legacy) */}
        <MetricCard
          title={callData.ConversationAnalytics?.Summary?.Scores?.qualityScore !== undefined ? "Quality Score" : "QA Score"}
          value={callData.ConversationAnalytics?.Summary?.Scores?.qualityScore !== undefined
            ? `${callData.ConversationAnalytics.Summary.Scores.qualityScore.toFixed(1)}%`
            : `${(callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score ?? 0)}/100`}
          status={
            (callData.ConversationAnalytics?.Summary?.Scores?.qualityScore ??
             callData.ConversationAnalytics?.Rating?.QA_Score ??
             callData.Result?.QA_Score ?? 0) >= 80 ? 'success' :
            (callData.ConversationAnalytics?.Summary?.Scores?.qualityScore ??
             callData.ConversationAnalytics?.Rating?.QA_Score ??
             callData.Result?.QA_Score ?? 0) >= 60 ? 'warning' : 'error'
          }
          icon={<FaChartBar />}
        />

        {/* Compliance Score Percentage */}
        {(callData.ConversationAnalytics?.Summary?.Scores?.complianceScore !== undefined ||
          callData.ConversationAnalytics?.Rating?.ComplianceScore !== undefined) && (
          <MetricCard
            title="Compliance Score"
            value={`${(callData.ConversationAnalytics?.Summary?.Scores?.complianceScore ??
                       callData.ConversationAnalytics?.Rating?.ComplianceScore ?? 0).toFixed(1)}%`}
            status={
              (callData.ConversationAnalytics?.Summary?.Scores?.complianceScore ??
               callData.ConversationAnalytics?.Rating?.ComplianceScore ?? 0) >= 80 ? 'success' :
              (callData.ConversationAnalytics?.Summary?.Scores?.complianceScore ??
               callData.ConversationAnalytics?.Rating?.ComplianceScore ?? 0) >= 60 ? 'warning' : 'error'
            }
            icon={<FaCheckCircle />}
          />
        )}

        {/* Compliance Pass/Fail Status */}
        <MetricCard
          title="Compliance"
          value={(callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'Pass' : 'Fail'}
          status={(callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'success' : 'error'}
          icon={<FaCheckCircle />}
        />

        {/* Critical Fail Indicator */}
        {callData.ConversationAnalytics?.Rating?.CriticalFail && (
          <MetricCard
            title="Critical Fail"
            value="FAILED"
            status="error"
            icon={<FaCheckCircle />}
            subtitle={callData.ConversationAnalytics?.Summary?.Scores?.criticalFailReason || 'Critical requirement not met'}
          />
        )}
        
        <MetricCard
          title="Duration"
          value={Formatter.Time(Math.round(callData?.ConversationAnalytics?.Duration ?? callData.duration))}
          status="neutral"
          icon={<FaStopwatch />}
        />
        
      </div>

      {/* Tab Navigation */}
      <div className="relative">
        {/* Floating Tab Navigation */}
        <div className="sticky top-4 z-50 flex justify-center mb-8">
          <div className="bg-white/90 backdrop-blur-sm px-2 py-2 rounded-full shadow-xl border border-gray-200 flex space-x-2">
            <button
              onClick={(e) => scrollToSection(e, 'overview')}
              className="px-6 py-2 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all active:scale-95"
            >
              Overview
            </button>
            <button
              onClick={(e) => scrollToSection(e, 'transcript')}
              className="px-6 py-2 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all active:scale-95"
            >
              Transcript
            </button>
            <button
              onClick={(e) => scrollToSection(e, 'qa')}
              className="px-6 py-2 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all active:scale-95"
            >
              QA
            </button>
          </div>
        </div>

        {/* Sections with IDs added */}
        <div id="overview" className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <h2 className="text-4xl font-bold mb-4">Overview</h2>
            <OverviewTab callData={callData} genAiSummary={genAiSummary} />
          </div>
        </div>

        <div id="transcript" className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <h2 className="text-4xl font-bold mb-4">Transcript</h2>
            <TranscriptTab
              callData={callData}
              speakerLabels={speakerLabels}
              audioRef={audioRef}
              transcriptRef={transcriptRef}
              deriveSentimentScore={deriveSegmentSentimentScore}
              onAudioPlayTimeUpdate={onAudioPlayTimeUpdate}
            /> 
          </div>
        </div>

        <div id="qa" className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <h2 className="text-4xl font-bold mb-4">QA</h2>
            <QATab
              callKey={key}
              userEmail={userEmail}
              callData={callData}
              onRefresh={(template) => refreshSummary(template)}
              setAlert={setAlert}
              campaign={callData?.ConversationAnalytics?.Cust}
              currentTemplate={callData?.ConversationAnalytics?.Template}
              summary={genAiSummary}
              transcript={callData.transcript}
              onRefr={() => refreshSummary()}
              history={genAiHistory}
              onSubmit={handleGenAiSubmit}              
            />  
          </div>
        </div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, status, icon, subtitle }) => {
  const statusColors = {
    success: 'text-green-600 bg-green-50',
    warning: 'text-yellow-600 bg-yellow-50',
    error: 'text-red-600 bg-red-50',
    neutral: 'text-gray-600 bg-gray-50'
  };

  return (
    <div className="card-hover">
      <div className="flex items-center">
        <div className={`flex-shrink-0 w-12 h-12 ${statusColors[status]} rounded-lg flex items-center justify-center text-2xl`}>
          {icon}
        </div>

        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
            {title}
          </p>
          <p className={`text-2xl font-bold ${statusColors[status].split(' ')[0]}`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-600 mt-1">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ callData, genAiSummary = [] }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Call Information</h3>
        <dl className="space-y-3">
          <div>
            <dt className="text-sm font-medium text-gray-500">Agent</dt>
            <dd className="text-sm text-gray-900">
              {callData?.ConversationAnalytics?.Agent || callData.agent}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Customer</dt>
            <dd className="text-sm text-gray-900">
              {callData?.ConversationAnalytics?.Cust || callData.customer}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Queue</dt>
            <dd className="text-sm text-gray-900">
              {callData?.ConversationAnalytics?.Queue || callData.queue}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Language</dt>
            <dd className="text-sm text-gray-900">
              {callData?.ConversationAnalytics?.LanguageCode || callData.lang}
            </dd>
          </div>
        </dl>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Call Summary</h3>
        <dl className="space-y-3">
          <div>
            <dt className="text-sm font-medium text-gray-500">Topic</dt>
            <dd className="text-sm text-gray-900">
              {callData.summary_topic || callData?.ConversationAnalytics?.ContactSummary?.Topic}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Product</dt>
            <dd className="text-sm text-gray-900">
              {callData.summary_product || callData?.ConversationAnalytics?.ContactSummary?.Product}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Resolved</dt>
            <dd className={`text-sm font-medium ${
              (callData.summary_resolved || callData?.ConversationAnalytics?.ContactSummary?.Resolved) === 'Yes'
                ? 'text-green-600'
                : 'text-red-600'
            }`}>
              {callData.summary_resolved || callData?.ConversationAnalytics?.ContactSummary?.Resolved}
            </dd>
          </div>
        </dl>
      </div>
    </div>

    {(callData.summary_summary ||
      callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content ||
      genAiSummary.length > 0) && (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
        {(
          callData.summary_summary ||
          callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content
        ) && (
          <p className="text-sm text-gray-700 leading-relaxed mb-4 text-left">
            {callData.summary_summary ||
              callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content}
          </p>
        )}
        {genAiSummary.length > 0 && (
          <dl className="space-y-3 text-left">
            {genAiSummary
              .filter((entry) => {
                const normalized = (entry.label ?? '')
                  .toString()
                  .trim()
                  .toLowerCase()
                  .replace(/:$/, '');
                return !['validations', 'transcription', 'contact summary', 'topic', 'product', 'resolved', 'actions'].includes(normalized);
              })
              .map((entry, i) => {
                const label = (entry.label ?? '').toString().trim();
                const normalizedLabel = label.toLowerCase().replace(/:$/, '');

                if (normalizedLabel === 'summary') {
                  return (
                    <div key={`summary-${i}`} className="text-sm text-gray-900 whitespace-pre-line">
                      {entry.value}
                    </div>
                  );
                }

                return (
                  <div key={`${normalizedLabel || 'entry'}-${i}`}>
                    <dt className="text-sm font-medium text-gray-500">{label}</dt>
                    <dd className="text-sm text-gray-900 whitespace-pre-line text-left">{entry.value}</dd>
                  </div>
                );
              })}
          </dl>
        )}
      </div>
    )}

    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Sentiment Analysis</h3>
      <div className="flex items-center space-x-6">
        <div className="flex items-center space-x-3">
          <SentimentIcon score={callData.callerSentimentScore} size="lg" />
          <div>
            <p className="text-sm font-medium text-gray-900">Customer Sentiment</p>
            <p className="text-sm text-gray-500">
              {callData.callerSentimentScore !== undefined && callData.callerSentimentScore !== null
                ? `${callData.callerSentimentScore}/100`
                : 'N/A'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <TrendIcon trend={callData.callerSentimentChange} size="lg" />
          <div>
            <p className="text-sm font-medium text-gray-900">Sentiment Change</p>
            <p className="text-sm text-gray-500 capitalize">
              {callData.callerSentimentChange || 'N/A'}
            </p>
          </div>
        </div>
      </div>
      
      <div className="mt-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Sentiment Score</p>
        <SentimentBar score={callData.callerSentimentScore} height="h-3" />
      </div>
    </div>
  </div>
);

// Transcript Tab
const TranscriptTab = ({ callData, speakerLabels, audioRef, transcriptRef, onAudioPlayTimeUpdate, deriveSentimentScore }) => {
  const hasTranslations = (callData?.SpeechSegments || []).some(s => s.TranslatedText);
  const [globalEnglish, setGlobalEnglish] = useState(false);

  return (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-medium text-gray-900">Call Transcript</h3>
      <div style={{display:"flex", alignItems:"center", gap:"0.75rem"}}>
        {hasTranslations && (
          <button
            style={{fontSize:"0.8rem", padding:"0.2rem 0.7rem", borderRadius:"0.25rem", border:"1px solid #ccc", background: globalEnglish ? "#f3f4f6" : "transparent", cursor:"pointer", color:"#374151"}}
            onClick={() => setGlobalEnglish(v => !v)}
          >
            {globalEnglish ? 'Show Zulu' : 'Show English'}
          </button>
        )}
        <div className="text-sm text-gray-500">
          Duration: {Formatter.Time(Math.round(callData?.ConversationAnalytics?.Duration || callData.duration))}
        </div>
      </div>
    </div>

    {callData.transcript && (
      <div className="card">
        <h4 className="text-md font-medium text-gray-900 mb-2">Transcription</h4>
        {/* <TranscriptionText text={callData.transcript} /> */}
      </div>
    )}

    {callData?.ConversationAnalytics?.SourceInformation?.[0]?.TranscribeJobInfo?.MediaFileUri && (
      <audio
        ref={audioRef}
        className="w-full"
        controls
        src={callData.ConversationAnalytics.SourceInformation[0].TranscribeJobInfo.MediaFileUri}
        onTimeUpdate={onAudioPlayTimeUpdate}
      >
        Your browser does not support the audio element.
      </audio>
    )}

    <div ref={transcriptRef} className="space-y-2">
      {(callData?.SpeechSegments || []).map((s, i) => {
        const segmentScore = deriveSentimentScore
          ? deriveSentimentScore(s)
          : (s.SentimentIsPositive - s.SentimentIsNegative);
        return (
          <TranscriptSegment
            key={i}
            name={speakerLabels[s.SegmentSpeaker] || s.SegmentSpeaker}
            allSegments={s.WordConfidence || []}
            segmentStart={s.SegmentStartTime}
            text={s.DisplayText}
            translatedText={s.TranslatedText || ''}
            globalEnglish={globalEnglish}
            onClick={(e) => {
              const t = e.target.dataset.currenttime;
              if (t && audioRef.current) audioRef.current.currentTime = t;
            }}
            highlightLocations={[
              ...(s.EntitiesDetected || []).map((e) => ({
                start: e.BeginOffset,
                end: e.EndOffset,
                fn: (match, key, start, end, oStart, oEnd) => (
                  <TranscriptOverlay
                    key={key}
                    colour={getEntityColor(e.Type)}
                    visuallyHidden={`Entity - ${e.Type}`}
                    data-start={start}
                    data-end={end}
                    data-offset-start={oStart}
                    data-offset-end={oEnd}
                    content={match}
                  />
                ),
              })),
            ]}
            score={segmentScore}
            interruption={s.SegmentInterruption}
            ivr={s.IVRSegment}
            categoryList={s.CategoriesDetected}
          />
        );
      })}

      {!callData?.SpeechSegments?.length && (
        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
          {callData.transcript || 'Transcript not available for this call.'}
        </p>
      )}
    </div>
  </div>
  );
};

// Analytics Tab
const AnalyticsTab = ({ callData, speakerLabels }) => {
  const sentimentData = callData?.ConversationAnalytics?.SentimentTrends;
  const speakerTime = callData?.ConversationAnalytics?.SpeakerTime;

  if (!sentimentData || !speakerTime) {
    return (
      <div className="text-center py-8">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No detailed analytics available</h3>
      </div>
    );
  }

  const speakerTimeData = Object.entries(speakerTime).map(([key, value]) => ({
    value: value.TotalTimeSecs,
    label: speakerLabels[key] || key,
    channel: key
  }));

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <SentimentChart data={sentimentData} speakerOrder={speakerLabels} />
      </div>
      <div className="bg-gray-50 rounded-lg p-4">
        <SpeakerTimeChart data={speakerTimeData} />
      </div>
    </div>
  );
};

// QA Tab

const QATab = ({ callKey, callData, onRefresh, setAlert, userEmail, campaign, currentTemplate, summary, transcript, onRefr, onSubmit, history }) => {
  const [makeEdit, setMakeEdit] = useState(false)
  const [editableCompliance, setEditableCompliance] = useState({});
  const [editableQuality, setEditableQuality] = useState({});
  const [isAssessed, setIsAssessed] = useState(callData?.ConversationAnalytics?.AssessDetails?.assessed || false);
  const [markingAssessed, setMarkingAssessed] = useState(false);
  const isSuperAdmin = useIsSuperAdmin();

  const validationsData = callData?.ConversationAnalytics?.Summary?.Validations || {};
  console.log("Validations: ", validationsData)
  const complianceItems = [];
  const qualityItems = [];

  if (validationsData && Object.keys(validationsData).length > 0) {
    Object.entries(validationsData).forEach(([key, value]) => {
      // Skip the Scores object - it's not a validation question
      if (key === 'Scores') {
        return;
      }

      try {
        const parsed = JSON.parse(value);

        // Use the 'type' field if available (v2.0+), otherwise fall back to legacy detection
        if (parsed.type) {
          // v2.0: Use explicit type field injected by backend
          if (parsed.type === 'Compliance') {
            complianceItems.push({ key, parsed });
          } else {
            qualityItems.push({ key, parsed });
          }
        } else {
          // Legacy: Detect based on presence of score or status fields
          if (parsed.score || !parsed.status) {
            qualityItems.push({ key, parsed });
          } else {
            complianceItems.push({ key, parsed });
          }
        }
      } catch (e) {
        // Skip invalid entries
        console.error(`Failed to parse validation ${key}:`, e);
      }
    });
  }

  useEffect(() => {
    if (complianceItems.length > 0) {
      const initialEditValues = {};
      complianceItems.forEach(({ key, parsed }) => {
        const isYesNo = parsed.answer !== undefined;

        initialEditValues[key] = {
          question: key,
          reason: parsed.reason || '',
          ...(isYesNo
            ? { answer: parsed.answer || 'no' }
            : { compliance: parsed.status || '' }
          )
        };
      });
      setEditableCompliance(initialEditValues);
    }
    if (qualityItems.length > 0) {
      const initialEditValues = {};
      qualityItems.forEach(({ key, parsed }) => {
        const isYesNo = parsed.answer !== undefined && parsed.score === undefined;

        initialEditValues[key] = {
          question: key,
          reason: parsed.reason || '',
          ...(isYesNo
            ? { answer: parsed.answer || 'no' }
            : { score: parsed.score || '' }
          )
        };
      });
      setEditableQuality(initialEditValues);
    }
  }, [complianceItems.length, qualityItems.length]);

  const handleComplianceChange = (key, field, value) => {
    setEditableCompliance(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleQualityChange = (key, field, value) => {
    setEditableQuality(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const getComplianceChanges = () => {
    let x = Object.values(editableCompliance)
    return x
  };

  const getQualityChanges = () => {
    let x = Object.values(editableQuality)
    return x
  };

  const getAllChanges = () => {
    return {
      changes: Object.values(editableCompliance, editableQuality)
    };
  };

  const handleSaveChanges = async () => {
    let body = [...getComplianceChanges(), ...getQualityChanges()]
    const changes = {changes : body, user: userEmail}

    try {
      const response = await override(callKey, changes)
      console.log("RESPONSE: ", response)

      console.log('Changes saved successfully');
      setMakeEdit(false);
      window.location.reload();
      
    } catch (error) {
      console.error('Error saving changes:', error);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: error.response?.data?.error || 'Failed to save changes'
      });
    }
  };

  const handleMarkAssessed = async () => {
    if (isAssessed) {
      setAlert({
        variant: 'warning',
        heading: 'Already Assessed',
        text: 'This call has already been marked as assessed.'
      });
      return;
    }

    if (!window.confirm('Are you sure you want to mark this call as assessed? This action cannot be undone and will prevent any future edits.')) {
      return;
    }

    setMarkingAssessed(true);
    try {
      const response = await markAssessed(callKey);
      console.log("MARKED AS ASSESSED: ", response);
      setIsAssessed(true);
      setAlert({
        variant: 'success',
        heading: 'Success',
        text: 'Call has been marked as assessed and is now locked from further edits.'
      });
    } catch (error) {
      console.error('Error marking as assessed:', error);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: error.response?.data?.error || 'Failed to mark call as assessed'
      });
    } finally {
      setMarkingAssessed(false);
    }
  };

  const handleUnmarkAssessed = async () => {
    if (!isAssessed) {
      setAlert({
        variant: 'warning',
        heading: 'Not Assessed',
        text: 'This call is not currently marked as assessed.'
      });
      return;
    }

    if (!window.confirm('Are you sure you want to unmark this call as assessed? This will allow edits and refreshes again.')) {
      return;
    }

    setMarkingAssessed(true);
    try {
      const response = await unmarkAssessed(callKey);
      console.log("UNMARKED AS ASSESSED: ", response);
      setIsAssessed(false);
      setAlert({
        variant: 'success',
        heading: 'Success',
        text: 'Call has been unmarked as assessed and can now be edited.'
      });
      // Refresh the page to update the UI
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error unmarking as assessed:', error);
      let errorMessage = 'Failed to unmark call as assessed';
      if (error.message?.includes('403') || error.message?.includes('Unauthorized')) {
        errorMessage = 'Unauthorized: Only SuperAdmin users can unmark assessed calls';
      }
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: errorMessage
      });
    } finally {
      setMarkingAssessed(false);
    }
  };

return (
  <div className="space-y-6">
    {isAssessed && (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center">
          <FaCheckCircle className="text-green-600 mr-2" />
          <div>
            <p className="text-sm font-medium text-green-900">This call has been marked as assessed</p>
            <p className="text-xs text-green-700 mt-1">No further edits or refreshes are allowed</p>
          </div>
        </div>
      </div>
    )}

    <div className="flex items-center justify-between">
      <h3 className="text-lg font-medium text-gray-900">Quality Assessment</h3>
      <div className="flex items-center gap-2">
        {!isAssessed && (
          <>
            <RefreshButton
              onRefresh={onRefresh}
              setAlert={setAlert}
              showTemplateDropdown={true}
              campaign={campaign}
              currentTemplate={currentTemplate}
              className="text-sm"
            />
            <button className='btn-secondary' onClick={() => setMakeEdit(true)}>
              Override
            </button>
            <button
              className='btn-primary bg-green-600 hover:bg-green-700'
              onClick={handleMarkAssessed}
              disabled={markingAssessed}
            >
              {markingAssessed ? 'Marking...' : 'Mark as Assessed'}
            </button>
          </>
        )}
        {isAssessed && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-green-100 text-green-800">
              <FaCheckCircle className="mr-1.5" />
              Assessed & Locked
            </span>
            {isSuperAdmin && (
              <button
                className='btn-secondary bg-orange-600 hover:bg-orange-700 text-white'
                onClick={handleUnmarkAssessed}
                disabled={markingAssessed}
              >
                {markingAssessed ? 'Unmarking...' : 'Unmark as Assessed'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="card md:col-span-2">
        {/* <h4 className="text-md font-medium text-gray-900 mb-3">Current Scores</h4>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Overall QA Score</dt>
            <dd className={`text-sm font-medium ${
              (callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score) >= 80 ? 'text-green-600' :
              (callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score) >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score ?? 0}/100
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Compliance Status</dt>
            <dd className={`text-sm font-medium ${
              (callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'text-green-600' : 'text-red-600'
            }`}>
              {(callData.ConversationAnalytics?.Rating?.Compliance ?? callData.Result?.Compliance) ? 'Pass' : 'Fail'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Assessment Status</dt>
            <dd className="text-sm text-gray-500">
              {(callData.ConversationAnalytics?.Rating?.QA_Score ?? callData.Result?.QA_Score) ? 'Completed' : 'Pending'}
            </dd>
          </div>

          {callData.ConversationAnalytics?.Rating?.CriticalFail && (
            <div className="pt-3 mt-3 border-t border-gray-200">
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <dt className="text-sm font-semibold text-red-700 flex items-center">
                  ⚠️ Critical Fail
                </dt>
                <dd className="text-xs text-red-600 mt-1">
                  {callData.ConversationAnalytics?.Summary?.Scores?.criticalFailReason ||
                   'One or more critical requirements were not met'}
                </dd>
              </div>
            </div>
          )}
          {isAssessed && (
            <>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <dt className="text-sm text-gray-500">Assessed By</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {callData.assessedBy || callData.ConversationAnalytics?.AssessDetails?.assessedBy || '-'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Assessed At</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {callData.assessedAt
                    ? Formatter.Timestamp(callData.assessedAt)
                    : (callData.ConversationAnalytics?.AssessDetails?.assessedAt
                        ? Formatter.Timestamp(callData.ConversationAnalytics.AssessDetails.assessedAt)
                        : '-')}
                </dd>
              </div>
            </>
          )}
        </dl> */}
        <GenAITab 
          summary={summary}
          transcript={transcript}
          onRefr={onRefr}
          history={history}
          onSubmit={onSubmit} 
          setAlert={setAlert}
        />
      </div>

      <div className="card md:col-span-2">
        <h4 className="text-md font-medium text-gray-900 mb-3">Assessment Details</h4>
        {(() => {
          
          if (validationsData && Object.keys(validationsData).length > 0) {
            
            return (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {(complianceItems.length > 0 && !makeEdit) && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Compliance</h5>
                    <ul className="space-y-3">
                      {complianceItems.map(({ key, parsed }) => {
                        const answer = parsed.answer?.toLowerCase();
                        const success = (parsed.success || parsed.successCriteria || 'yes').toLowerCase();
                        const passed = parsed.passed !== undefined ? parsed.passed : (answer === success);
                        const isViolation = !passed;

                        return (
                          <li key={key} className={`text-xs border-b pb-2 ${
                            isViolation ? 'border-red-300 bg-red-50 p-2 rounded' : 'border-gray-100'
                          }`}>
                            <div className="font-medium capitalize mb-1">{key}</div>
                            {/* Display the assessment/question text */}
                            {parsed.assessment && (
                              <div className="text-xs text-gray-600 italic mb-1 ml-3">
                                "{parsed.assessment}"
                              </div>
                            )}
                            <ul className="ml-3 space-y-0.5">
                              {/* Show answer field for v2.0, status for legacy */}
                              {answer ? (
                                <>
                                  <li className="flex items-center">
                                    • Answer:
                                    <span className={`ml-1 font-semibold ${
                                      passed ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                      {answer.toUpperCase()}
                                    </span>
                                    {isViolation && (
                                      <span className="ml-2 text-xs text-red-600">⚠️ VIOLATION</span>
                                    )}
                                  </li>
                                  <li className="text-xs text-gray-500">
                                    • Success Criteria: {success.toUpperCase()}
                                  </li>
                                </>
                              ) : parsed.status ? (
                                <li>• Status: <span className={
                                  parsed.status === 'passed' || parsed.status === 'success'
                                    ? 'text-green-600 font-medium'
                                    : 'text-red-600 font-medium'
                                }>{parsed.status}</span></li>
                              ) : null}
                              <li>• Reason: {parsed.reason}</li>
                              {parsed.evidence?.agent && <li>• Agent: "{parsed.evidence.agent}"</li>}
                              {parsed.evidence?.customer && <li>• Customer: "{parsed.evidence.customer}"</li>}
                            </ul>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {(complianceItems.length > 0 && makeEdit && !isAssessed) && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Compliance (Edit Mode)</h5>
                    <ul className="space-y-4">
                      {complianceItems.map(({ key, parsed }) => {
                        const isYesNo = parsed.answer !== undefined;
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;

                        return (
                          <li key={key} className="text-xs border border-gray-200 rounded p-3 bg-gray-50">
                            <div className="font-medium capitalize mb-2 flex items-center justify-between">
                              <span>{key}</span>
                              {parsed.complianceViolation && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">
                                  VIOLATION
                                </span>
                              )}
                            </div>
                            {/* Display the assessment/question text */}
                            {parsed.assessment && (
                              <div className="text-xs text-gray-600 italic mb-2 ml-3">
                                "{parsed.assessment}"
                              </div>
                            )}
                            <div className="ml-3 space-y-2">
                              {/* Answer/Status Field */}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  {isYesNo ? 'Answer:' : 'Status:'}
                                </label>
                                {isYesNo ? (
                                  /* Yes/No Dropdown */
                                  <>
                                    <select
                                      value={editableCompliance[key]?.answer || 'no'}
                                      onChange={(e) => handleComplianceChange(key, 'answer', e.target.value)}
                                      className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      disabled={isAssessed}
                                    >
                                      <option value="yes">Yes</option>
                                      <option value="no">No</option>
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Success is determined by the 'success' field in the question.
                                      {parsed.success && ` (Expected: ${parsed.success.toUpperCase()})`}
                                    </p>
                                  </>
                                ) : (
                                  /* Legacy Passed/Failed Dropdown */
                                  <select
                                    value={editableCompliance[key]?.compliance || 'passed'}
                                    onChange={(e) => handleComplianceChange(key, 'compliance', e.target.value)}
                                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    disabled={isAssessed}
                                  >
                                    <option value="passed">Passed</option>
                                    <option value="failed">Failed</option>
                                  </select>
                                )}
                              </div>

                              {/* Reason Input */}
                              <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                  Reason:
                                </label>
                                <textarea
                                  value={editableCompliance[key]?.reason || ''}
                                  onChange={(e) => handleComplianceChange(key, 'reason', e.target.value)}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                                  rows="2"
                                  placeholder="Enter reason..."
                                  disabled={isAssessed}
                                />
                              </div>

                              {/* Read-only evidence display */}
                              {parsed.evidence?.agent && (
                                <div className="text-gray-600">• Agent: "{parsed.evidence.agent}"</div>
                              )}
                              {parsed.evidence?.customer && (
                                <div className="text-gray-600">• Customer: "{parsed.evidence.customer}"</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    
                    {/* Preview of changes (optional, for debugging) */}
                    {/* <div className="mt-4 p-2 bg-blue-50 rounded">
                      <h6 className="text-sm font-medium text-blue-800 mb-1">Changes Preview:</h6>
                      <pre className="text-sm text-blue-700">
                        {JSON.stringify(getComplianceChanges(), null, 2)}
                      </pre>
                    </div> */}
                  </div>
                )}


                {(qualityItems.length > 0 && !makeEdit) && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Quality Assurance</h5>
                    <ul className="space-y-3">
                      {qualityItems.map(({ key, parsed }) => {
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;
                        const answer = parsed.answer?.toLowerCase();
                        const success = (parsed.success || parsed.successCriteria || 'yes').toLowerCase();
                        const isCorrect = answer === success;
                        const failed = (!isCorrect && isCriticalFail) || parsed.status === 'failed';

                        return (
                          <li key={key} className={`text-sm border-b pb-2 ${
                            failed ? 'border-red-300 bg-red-50 p-2 rounded' : 'border-gray-100'
                          }`}>
                            <div className="font-medium capitalize mb-1 flex items-center justify-between">
                              <span>{key}</span>
                              {isCriticalFail && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">
                                  CRITICAL
                                </span>
                              )}
                            </div>
                            {/* Display the assessment/question text */}
                            {parsed.assessment && (
                              <div className="text-xs text-gray-600 italic mb-1 ml-3">
                                "{parsed.assessment}"
                              </div>
                            )}
                            <ul className="ml-3 space-y-0.5">
                              {answer && (
                                <li className="flex items-center">
                                  • Answer:
                                  <span className={`ml-1 font-semibold ${
                                    isCorrect ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {answer.toUpperCase()}
                                  </span>
                                  {failed && (
                                    <span className="ml-2 text-xs text-red-600">⚠️ FAILED</span>
                                  )}
                                </li>
                              )}
                              {/* Show QA weighting score for yes/no questions */}
                              {parsed.qaWeightAchieved !== undefined && parsed.qaWeighting !== undefined && (
                                <li className="flex items-center">
                                  • Score:
                                  <span className={`ml-1 font-semibold ${
                                    isCorrect ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {parsed.qaWeightAchieved} / {parsed.qaWeighting}
                                  </span>
                                  <span className="text-xs text-gray-500 ml-1">points</span>
                                </li>
                              )}
                              {/* Show legacy score for scored questions */}
                              {parsed.score !== undefined && <li>• Score: {parsed.score} / {parsed.weight}</li>}
                              <li>• Reason: {parsed.reason}</li>
                              {parsed.evidence?.agent && <li>• Agent: "{parsed.evidence.agent}"</li>}
                              {parsed.evidence?.customer && <li>• Customer: "{parsed.evidence.customer}"</li>}
                            </ul>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {(qualityItems.length > 0 && makeEdit && !isAssessed) && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Quality (Edit Mode)</h5>
                    <ul className="space-y-4">
                      {qualityItems.map(({ key, parsed }) => {
                        const isYesNo = parsed.answer !== undefined && parsed.score === undefined;
                        const isCriticalFail = parsed.criticalFail === true || parsed.isCriticalFail === true;

                        return (
                          <li key={key} className="text-sm border border-gray-200 rounded p-3 bg-gray-50">
                            <div className="font-medium capitalize mb-2 flex items-center justify-between">
                              <span>{key}</span>
                              {isCriticalFail && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">
                                  CRITICAL
                                </span>
                              )}
                            </div>
                            {/* Display the assessment/question text */}
                            {parsed.assessment && (
                              <div className="text-xs text-gray-600 italic mb-2 ml-3">
                                "{parsed.assessment}"
                              </div>
                            )}
                            <div className="ml-3 space-y-2">
                              {/* Answer/Score Field */}
                              <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                  {isYesNo ? 'Answer:' : 'Score:'}
                                </label>
                                {isYesNo ? (
                                  /* Yes/No Dropdown */
                                  <>
                                    <select
                                      value={editableQuality[key]?.answer || 'no'}
                                      onChange={(e) => handleQualityChange(key, 'answer', e.target.value)}
                                      className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      disabled={isAssessed}
                                    >
                                      <option value="yes">Yes</option>
                                      <option value="no">No</option>
                                    </select>
                                    {isCriticalFail && (
                                      <p className="text-xs text-red-500 mt-1">
                                        ⚠️ Critical: "No" will fail the entire scorecard
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  /* Numeric Score Input */
                                  <>
                                    <input
                                      type="number"
                                      value={editableQuality[key]?.score || ''}
                                      onChange={(e) => handleQualityChange(key, 'score', e.target.value)}
                                      className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      disabled={isAssessed}
                                    />
                                    {parsed.weight !== undefined && (
                                      <span className="text-xs text-gray-600 ml-2">Out of {parsed.weight}</span>
                                    )}
                                  </>
                                )}
                              </div>

                              {/* Reason Input */}
                              <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                  Reason:
                                </label>
                                <textarea
                                  value={editableQuality[key]?.reason || ''}
                                  onChange={(e) => handleQualityChange(key, 'reason', e.target.value)}
                                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                                  rows="2"
                                  placeholder="Enter reason..."
                                  disabled={isAssessed}
                                />
                              </div>

                              {/* Read-only evidence display */}
                              {parsed.evidence?.agent && (
                                <div className="text-gray-600">• Agent: "{parsed.evidence.agent}"</div>
                              )}
                              {parsed.evidence?.customer && (
                                <div className="text-gray-600">• Customer: "{parsed.evidence.customer}"</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    
                    {/* Preview of changes (optional, for debugging) */}
                    {/* <div className="mt-4 p-2 bg-blue-50 rounded">
                      <h6 className="text-sm font-medium text-blue-800 mb-1">Changes Preview:</h6>
                      <pre className="text-sm text-blue-700">
                        {JSON.stringify(getQualityChanges(), null, 2)}
                      </pre>
                    </div> */}
                  </div>
                )}

                {makeEdit && !isAssessed && (
                  <>
                  <button className='btn-secondary' onClick={() => handleSaveChanges()}>
                    Save Changes
                  </button><br/>
                  <button className='btn-primary' onClick={() => setMakeEdit(false)}>
                    Cancel
                  </button>
                  </>

                )}
              </div>
            );
          } else {
            return (
              <div className="text-center py-4">
                <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No assessment details available</p>
              </div>
            );
          }
        })()}
      </div>
    </div>

    <div className="bg-rewardsco-50 rounded-lg p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-rewardsco-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-rewardsco-800">
            Quality Assessment Guidelines
          </h3>
          <div className="mt-2 text-sm text-rewardsco-700">
            <ul className="list-disc list-inside space-y-1">
              <li>Listen to the complete call recording before assessment</li>
              <li>Evaluate each criterion objectively using the scoring rubric</li>
              <li>Provide specific feedback and examples where applicable</li>
              <li>Consider both agent performance and customer satisfaction</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);
  }

const TranscriptionText = ({ text }) => {
  const [showFull, setShowFull] = useState(false);
  if (!text) return null;
  const cutoff = 300;
  const shouldTruncate = text.length > cutoff;
  const display = shouldTruncate && !showFull ? text.substring(0, cutoff) + '...' : text;

  return (
    <div className="mt-2">
      <pre className="whitespace-pre-wrap text-sm text-gray-800">{display}</pre>
      {shouldTruncate && (
        <button
          className="text-sm text-rewardsco-600 underline"
          onClick={() => setShowFull((s) => !s)}
        >
          {showFull ? 'Show less' : 'See more'}
        </button>
      )}
    </div>
  );
};

const GenAITab = ({ summary, transcript, onRefr, history, onSubmit, setAlert }) => (
  <div className="space-y-6">
    {/* <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-gray-900">Summary</h3>
        <RefreshButton onRefresh={onRefr} setAlert={setAlert} className="text-sm" />
      </div>
      {summary.length > 0 ? (
        <dl className="space-y-2">
          {summary
            .filter(
              (e) =>
                e.label !== 'Validations' &&
                e.label !== 'Transcription' &&
                e.label !== 'Contact Summary' &&
                e.label !== 'Topic' &&
                e.label !== 'Product' &&
                e.label !== 'Resolved' &&
                e.label !== 'Actions'
            )
            .map((entry, i) => (
              <div key={i} className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">{entry.label}</dt>
                <dd className="text-sm text-gray-900 text-right whitespace-pre-line">{entry.value}</dd>
              </div>
            ))}
        </dl>
      ) : (
        <p className="text-sm text-gray-500">No Summary Available</p>
      )}
      <TranscriptionText text={transcript} />
    </div> */}
    <div className="space-y-3">
      <h4 className="text-lg font-medium text-gray-900">Generative AI Query</h4>
      {history.length === 0 ? (
        <p className="text-sm text-gray-500">Ask a question about this call.</p>
      ) : (
        history.map((entry, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-3">
            <p className="font-medium text-gray-800">{entry.question}</p>
            <p className="text-gray-700 mt-1 whitespace-pre-line">{entry.answer}</p>
          </div>
        ))
      )}
      <ChatInput onSubmit={onSubmit} />
    </div>
  </div>
);

export default Dashboard;
